import React, { useId, useRef, useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import { MarkButton, MarkHeading } from '../MarkControls';
import MarkModal from '../MarkModal';
import type { Block, Channel } from '../../types';
import { SMS_ALLOWED_BLOCKS, NESTING_BLOCK_TYPES, NESTING_LIMIT } from './constants';
import { CONTENT_CHOICES, EMPTY_CONTENT } from './contentChoices';
import * as styles from './canvas.module.css';

type Props = {
    onInsert: (block: Block) => void;
    channel: Channel;
    depth?: number;
    position: number;
    parentLabel?: string;
    showLabel?: boolean;
    triggerLabel?: string;
};
const PRIMARY: Block['type'][] = ['paragraph', 'heading', 'image', 'button'];
const ContentSample: React.FC<{ type: Block['type'] }> = ({ type }) => (
    <span className={styles.sample} aria-hidden="true">
        {type === 'heading' ? (
            <strong>A clear heading</strong>
        ) : type === 'button' ? (
            <span className={styles.sampleButton}>Read more</span>
        ) : type === 'image' ? (
            <span>▧ Image</span>
        ) : type === 'two_column' ? (
            <span>Left column | Right column</span>
        ) : type === 'divider' ? (
            <span className={styles.sampleLine} />
        ) : type === 'spacer' ? (
            <span>↕ More space</span>
        ) : type === 'conditional' ? (
            <span>If a detail matches…</span>
        ) : (
            <>
                <span className={styles.sampleLine} />
                <span className={styles.sampleLine} style={{ width: '65%' }} />
                <span className={styles.sampleLine} style={{ width: '75%' }} />
            </>
        )}
    </span>
);
const ContentPicker: React.FC<Props> = ({
    onInsert,
    channel,
    depth = 0,
    position,
    parentLabel,
    showLabel = false,
    triggerLabel
}) => {
    const [open, setOpen] = useState(false);
    const [category, setCategory] = useState<'Content' | 'Layout' | 'Advanced'>('Content');
    const titleId = useId();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const where = parentLabel ? ` in ${parentLabel}` : '';
    const close = () => {
        setOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
    };
    const choices = (types: Block['type'][]) => (
        <div className={styles.pickerGrid}>
            {types.map((type) => {
                const info = CONTENT_CHOICES[type];
                const smsDisabled =
                    channel === 'sms' && !(SMS_ALLOWED_BLOCKS as readonly string[]).includes(type);
                const depthDisabled =
                    depth >= NESTING_LIMIT &&
                    (NESTING_BLOCK_TYPES as readonly string[]).includes(type);
                return (
                    <MarkButton
                        key={type}
                        className={styles.pickerCard}
                        shape="rounded"
                        size="regular"
                        disabled={smsDisabled || depthDisabled}
                        aria-label={`Add ${info.label.toLowerCase()}`}
                        onClick={() => {
                            setOpen(false);
                            onInsert(JSON.parse(JSON.stringify(EMPTY_CONTENT[type])) as Block);
                        }}
                    >
                        <span className={styles.pickerCopy}>
                            <ContentSample type={type} />
                            <P noDefaultMargin strong>
                                {info.label}
                            </P>
                            <P noDefaultMargin size="small">
                                {info.description}
                            </P>
                            {(smsDisabled || depthDisabled) && (
                                <P noDefaultMargin size="small">
                                    {smsDisabled
                                        ? 'Available for email only.'
                                        : 'This layout cannot be nested any further.'}
                                </P>
                            )}
                        </span>
                    </MarkButton>
                );
            })}
        </div>
    );
    return (
        <>
            <MarkButton
                ref={triggerRef}
                size={showLabel ? 'regular' : 'small'}
                aria-label={`Add content at position ${position}${where}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => {
                    setCategory('Content');
                    setOpen(true);
                }}
            >
                {triggerLabel ?? (showLabel ? '+ Add content' : '+')}
            </MarkButton>
            {open && (
                <MarkModal
                    aria-labelledby={titleId}
                    onClose={close}
                    styles={{ width: 'min(80rem, 94vw)' }}
                >
                    <MarkHeading id={titleId} role="h4">
                        Add content
                    </MarkHeading>
                    <P noDefaultMargin>
                        Choose what to add to your {channel === 'sms' ? 'message' : 'email'}.
                    </P>
                    <div className={styles.actions} role="group" aria-label="Content categories">
                        {(['Content', 'Layout', 'Advanced'] as const).map((item) => (
                            <MarkButton
                                key={item}
                                size="regular"
                                aria-pressed={category === item}
                                onClick={() => setCategory(item)}
                            >
                                {item}
                            </MarkButton>
                        ))}
                    </div>
                    {category === 'Content' ? (
                        <>
                            {choices(PRIMARY)}
                            <details className={styles.moreActions}>
                                <summary>More content</summary>
                                <div style={{ marginTop: '1.6rem' }}>
                                    {choices(['list', 'testimonial'])}
                                </div>
                            </details>
                        </>
                    ) : (
                        choices(
                            (Object.keys(CONTENT_CHOICES) as Block['type'][]).filter(
                                (type) => CONTENT_CHOICES[type].category === category
                            )
                        )
                    )}
                    <div className={styles.actions}>
                        <MarkButton size="regular" onClick={close}>
                            Cancel
                        </MarkButton>
                    </div>
                </MarkModal>
            )}
        </>
    );
};
export default ContentPicker;
